/**
 * LinearGradient - Web implementation using CSS gradients
 * Replaces react-native-linear-gradient for web platform
 */

import React, { useMemo } from 'react';
import { View, StyleSheet, ViewStyle, StyleProp } from 'react-native';

interface LinearGradientProps {
  colors: string[];
  start?: { x: number; y: number };
  end?: { x: number; y: number };
  locations?: number[];
  style?: StyleProp<ViewStyle>;
  children?: React.ReactNode;
  useAngle?: boolean;
  angle?: number;
  angleCenter?: { x: number; y: number };
}

const LinearGradient: React.FC<LinearGradientProps> = ({
  colors,
  start = { x: 0, y: 0 },
  end = { x: 1, y: 0 },
  locations,
  style,
  children,
  useAngle = false,
  angle = 0,
}) => {
  const gradientStyle = useMemo(() => {
    // Calculate angle from start/end points if not using explicit angle
    let gradientAngle: number;
    if (useAngle) {
      gradientAngle = angle;
    } else {
      // Convert start/end points to angle
      const dx = end.x - start.x;
      const dy = end.y - start.y;
      gradientAngle = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
    }

    // Build color stops
    const colorStops = colors.map((color, index) => {
      if (locations && locations[index] !== undefined) {
        return `${color} ${locations[index] * 100}%`;
      }
      // Distribute colors evenly if no locations provided
      const position = colors.length > 1 ? (index / (colors.length - 1)) * 100 : 0;
      return `${color} ${position}%`;
    });

    return {
      backgroundImage: `linear-gradient(${gradientAngle}deg, ${colorStops.join(', ')})`,
    };
  }, [colors, start, end, locations, useAngle, angle]);

  return (
    <View style={[styles.container, style, gradientStyle as ViewStyle]}>
      {children}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    // Ensure the gradient container can display its background
  },
});

export default LinearGradient;

// Named export for compatibility
export { LinearGradient };
